import type { Addon, Permission, Plan } from './types';

/**
 * designer is renamed to Pro plan
 * agency is renamed to Expert
 */

/**
 * Single source of truth for all feature access control.
 *
 * Maps every Permission to the Plans that unlock it.
 * To gate a new feature: add one entry here. Nothing else in the permission
 * layer needs to change.
 *
 * Plan hierarchy (lowest → highest):
 *   free → diy → designer (Pro) → agency (Expert) → enterprise
 *   localseo — separate user type, listed explicitly where it applies
 *
 * Source of truth: https://www.seospace.co/#pricing
 *
 * Add-on note: 'backlinks.view' and 'keyword.tracking' are purchasable
 * add-ons for diy/designer plans. Including them here means the route is
 * accessible — the component handles whether the add-on is actually active.
 */
export const PERMISSION_MATRIX: Record<Permission, Plan[]> = {
  // ── Keyword Research ──────────────────────────────────────────────────────
  'keyword.explorer':   ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],
  'keyword.tracking':   ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],
  'keyword.competitor': ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],
  'keyword.blog_ideas': ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],
  'keyword.lists':      ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],

  // ── Site Audit ────────────────────────────────────────────────────────────
  'site.audit':         ['free', 'diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],
  // Pricing: DIY Monthly monitoring, Pro Monthly, Expert Weekly
  'site.audit.reaudit': ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],
  // Pricing: "Scan Live Pages" ✓ for all plans including Free
  'site.scanned_pages': ['free', 'diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],

  // ── Local SEO ─────────────────────────────────────────────────────────────
  'local.hub':                   ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.business_profile':      ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.map_rank_tracker':      ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.google_reviews':        ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.google_reviews.widget': ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.business_posts':        ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.listing_manager':       ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'local.citations':             ['enterprise', 'localseo'],
  'local.tasks':                 ['designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],

  // ── AI & Reports ──────────────────────────────────────────────────────────
  'ai.visibility': ['agency', 'expertplus', 'enterprise'],
  'report.pdf':    ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'], // PDF Reports ✓ for DIY, Pro, Expert

  // ── Lead Generation ───────────────────────────────────────────────────────
  'leads.form_builder': ['agency', 'expertplus', 'enterprise'],

  // ── Team ──────────────────────────────────────────────────────────────────
  // Note: members are always blocked from this regardless of plan (role check in usePermissions)
  'team.user_management': ['designer', 'proplus', 'agency', 'expertplus', 'enterprise'],

  // ── Backlinks ─────────────────────────────────────────────────────────────
  'backlinks.view': ['diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise'],

  // ── Account — accessible to all authenticated users ───────────────────────
  'account.billing':  ['free', 'diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],
  'account.settings': ['free', 'diy', 'designer', 'proplus', 'agency', 'expertplus', 'enterprise', 'localseo'],

  // ── Site Transfer ─────────────────────────────────────────────────────────
  'site-transfer': ['agency', 'expertplus', 'enterprise'],
};

/**
 * Add-on → Permission unlock map.
 *
 * Lists which permissions each purchasable add-on unlocks.
 * This is checked as a fallback inside usePermissions.can() when the user's
 * base plan does not grant access.
 *
 * Only feature-unlock add-ons live here. Consumable/quota add-ons
 * (extra scan credits, extra keyword credits) are not listed because they
 * don't change what features a user can access — only how much they can use.
 */
export const ADDON_PERMISSIONS: Partial<Record<Permission, Addon[]>> = {
  // AI Visibility is included in agency/enterprise base plans.
  // diy/designer users can purchase it as a standalone add-on.
  'ai.visibility': ['addon.ai_visibility'],

  // Rank Tracking is a $20 add-on for diy and designer plans.
  // agency+ gets it included in the base plan.
  'keyword.tracking': ['addon.keyword_tracking'],

  // Backlinks is a $20 add-on for diy and designer plans.
  // agency+ gets it included in the base plan.
  'backlinks.view': ['addon.backlinks'],

  // Local SEO add-on (purchased via UpgradeAgencyPlanModal, isLocalSeoUnlock flow).
  // Unlocks the full Local SEO feature set for diy/designer plans.
  // All local.* permissions that designer+ gets are unlocked by this one add-on.
  'local.hub':                   ['addon.localseo'],
  'local.business_profile':      ['addon.localseo'],
  'local.map_rank_tracker':      ['addon.localseo'],
  'local.google_reviews':        ['addon.localseo'],
  'local.google_reviews.widget': ['addon.localseo'],
  'local.business_posts':        ['addon.localseo'],
  'local.listing_manager':       ['addon.localseo'],
  'local.tasks':                 ['addon.localseo'],
  // local.citations is NOT included — it stays enterprise/localseo plan only.
};
